import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  finalizeWithdrawal,
  getBridges,
  provenWithdrawal,
} from '../../api/bridge.api';
import { useInfiniteQuery } from '@tanstack/react-query';
import LoadingButton from '../common/LoadingButton';
import { Address, TransactionReceipt, createWalletClient, custom } from 'viem';
import { checkNetwork } from '../../shared/utils/CheckNetwork';
import {
  publicClientL1,
  publicClientL2,
} from '../../shared/constants/ViemConfig';
import { getTransactionReceipt, waitForTransactionReceipt } from 'viem/actions';
import { getWithdrawals } from 'viem/op-stack';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { walletActionsL1 } from 'viem/op-stack';
import moment from 'moment';
import VirtualTokenLogo from '../../assets/images/virtual-token.svg';
import { useInView } from 'react-intersection-observer';
import { useVirtualBalanceContext } from '../../context/VirtualBalanceContext';
import { enqueueSnackbar } from 'notistack';
import { getTokenNetwork } from '../../shared/utils/TokenNetwork';

interface Props {
  isLoading: (loading: boolean) => void;
}

const BridgeHistory: React.FC<Props> = (props) => {
  const { user } = usePrivy();
  const { wallets } = useWallets();
  const { fetchNativeVirtualBalance } = useVirtualBalanceContext();
  const { ref, inView } = useInView();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timestamp, setTimestamp] = useState<number>(0);
  const [selectedTxnHash, setSelectedTxnHash] = useState<string>('');

  const { fetchNextPage, data } = useInfiniteQuery({
    queryKey: ['bridgeHistory', timestamp],
    queryFn: ({ pageParam }) =>
      getBridges({
        page: pageParam,
        pageSize: 10,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.meta.pagination.page + 1;

      return nextPage > lastPage.meta.pagination.pageCount
        ? undefined
        : nextPage;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return firstPage.meta.pagination.page - 1;
    },
    refetchOnWindowFocus: false,
  });

  const getWithdrawalStatus = async (receipt: TransactionReceipt) => {
    return await publicClientL1.getWithdrawalStatus({
      receipt,
      targetChain: publicClientL2.chain,
    });
  };

  useEffect(() => {
    props.isLoading(isLoading);
  }, [props, isLoading]);

  const proveTxn = async (txHash: Address) => {
    try {
      setSelectedTxnHash(txHash);
      setIsLoading(true);

      await checkNetwork(getTokenNetwork());

      const receipt = await getTransactionReceipt(publicClientL2, {
        hash: txHash,
      });

      const timeToProveData = await publicClientL1.getTimeToProve({
        receipt,
        targetChain: publicClientL2.chain,
      });

      if (timeToProveData.timestamp) {
        return enqueueSnackbar({
          message: `You may prove this withdrawal in approx. ${moment(
            timeToProveData.timestamp
          ).fromNow()}`,
          variant: 'info',
        });
      }

      const status = await getWithdrawalStatus(receipt);

      if (status === 'ready-to-finalize') {
        await provenWithdrawal(txHash, '0x');
        setTimestamp(new Date().valueOf());

        return;
      }

      const output = await publicClientL1.getL2Output({
        l2BlockNumber: receipt.blockNumber,
        targetChain: publicClientL2.chain,
      });

      const [withdrawal] = getWithdrawals(receipt);

      const args2 = await publicClientL2.buildProveWithdrawal({
        chain: publicClientL2.chain,
        account: user?.wallet?.address as `0x${string}`,
        output,
        withdrawal,
      });

      const wallet = wallets[0];
      const provider = await wallet.getEthereumProvider();

      const walletClientL1 = createWalletClient({
        chain: getTokenNetwork(),
        transport: custom(provider),
      }).extend(walletActionsL1());

      const proveTxHash = await walletClientL1.proveWithdrawal({
        ...args2,
        authorizationList: [], // Add an appropriate authorization list here
      });

      await provenWithdrawal(txHash, proveTxHash);

      setTimestamp(new Date().valueOf());

      enqueueSnackbar({
        message:
          'Successfully proved withdraw transaction, check history for updated status',
        variant: 'success',
      });
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar({
        message: 'Prove withdrawal failed',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
      setSelectedTxnHash('');
    }
  };

  const finalizeTxn = async (txHash: Address) => {
    try {
      setSelectedTxnHash(txHash);
      setIsLoading(true);

      const receipt = await getTransactionReceipt(publicClientL2, {
        hash: txHash,
      });

      const status = await getWithdrawalStatus(receipt);

      if (status !== 'ready-to-finalize' && status !== 'finalized') {
        enqueueSnackbar({
          message:
            `Status: ${status}. Transaction is not ready to be finalized yet, please check back later`,
          variant: 'error',
        });

        return;
      } else if (status === 'finalized') {
        await finalizeWithdrawal(txHash, '0x');
        setTimestamp(new Date().valueOf());
      } else if (status === 'ready-to-finalize') {
        const [withdrawal] = getWithdrawals(receipt);

        await publicClientL1.waitToFinalize({
          withdrawalHash: withdrawal.withdrawalHash,
          targetChain: publicClientL2.chain,
        });

        await checkNetwork(getTokenNetwork());

        const wallet = wallets[0];
        const provider = await wallet.getEthereumProvider();

        const walletClientL1 = createWalletClient({
          chain: getTokenNetwork(),
          transport: custom(provider),
        }).extend(walletActionsL1());

        const finalizeTxnHash = await walletClientL1.finalizeWithdrawal({
          account: user?.wallet?.address as `0x${string}`,
          targetChain: publicClientL2.chain,
          withdrawal,
          authorizationList: [], // Add an appropriate authorization list here
        });

        await finalizeWithdrawal(txHash, finalizeTxnHash);

        await waitForTransactionReceipt(publicClientL1, {
          hash: finalizeTxnHash,
        });

        fetchNativeVirtualBalance();

        setTimestamp(new Date().valueOf());

        enqueueSnackbar({
          message: 'Successfully bridged your VIRTUAL to Ethereum',
          variant: 'success',
        });
      }
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar({
        message: `Finalize withdrawal failed ${error.message}`,
        variant: 'error',
      });
    } finally {
      setSelectedTxnHash('');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  return (
    <Box
      padding={2}
      borderRadius={3}
      sx={{
        maxHeight: 200,
        overflowY: 'scroll',
        backgroundColor: '#FBFBFB',
      }}
    >
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <td align="right" style={{ paddingRight: 30 }}>
              <Typography fontSize={14} sx={{ opacity: 0.5 }} pb={1}>
                Amount
              </Typography>
            </td>
            <td align="left">
              <Typography fontSize={14} sx={{ opacity: 0.5 }} pb={1}>
                Action
              </Typography>
            </td>
            <td align="right">
              <Typography fontSize={14} sx={{ opacity: 0.5 }} pb={1}>
                Status
              </Typography>
            </td>
          </tr>
        </thead>
        <tbody>
          {data?.pages.map((page) => {
            return page.data.map((data) => {
              return (
                <tr>
                  <td
                    align="right"
                    width="35%"
                    style={{ whiteSpace: 'nowrap', paddingRight: 30 }}
                  >
                    <Box
                      display="flex"
                      gap={1}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Typography fontWeight="bold">
                        {data.amount.toLocaleString('en-US')}{' '}
                      </Typography>
                      <img
                        src={VirtualTokenLogo}
                        width={20}
                        height={20}
                        alt="virtual"
                      />
                    </Box>
                  </td>
                  <td align="left">
                    <Typography style={{ textTransform: 'capitalize' }}>
                      {data.type?.toLowerCase()}
                    </Typography>
                  </td>
                  <td align="right" width="1%" style={{ whiteSpace: 'nowrap' }}>
                    {(data.finalizeTxnHash || data.type === 'DEPOSIT') && (
                      <Typography>Submitted</Typography>
                    )}

                    {data.timeToFinalize && !data.finalizeTxnHash && (
                      <LoadingButton
                        loading={data.bridgeTxnHash === selectedTxnHash}
                        size="small"
                        sx={{ paddingY: 0, minHeight: 0 }}
                        variant="contained"
                        disabled={isLoading}
                        onClick={async () =>
                          await finalizeTxn(data.bridgeTxnHash)
                        }
                      >
                        Finalize
                      </LoadingButton>
                    )}

                    {data.timeToProve && !data.timeToFinalize && (
                      <>
                        {moment().isAfter(data.timeToProve) ? (
                          <LoadingButton
                            loading={data.bridgeTxnHash === selectedTxnHash}
                            size="small"
                            sx={{ paddingY: 0, minHeight: 0 }}
                            variant="contained"
                            disabled={isLoading}
                            onClick={async () =>
                              await proveTxn(data.bridgeTxnHash)
                            }
                          >
                            Prove Withdrawal
                          </LoadingButton>
                        ) : (
                          <Typography>
                            Prove {moment(data.timeToProve).fromNow()}
                          </Typography>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            });
          })}
          <tr>
            <td colSpan={3}>
              <div ref={ref}></div>
            </td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default BridgeHistory;
